import { reactive } from 'vue';
import makeStoreDebugAccessible from '@/core/store/store-debug';
import {
    B2BContentPageContent,
    B2BContentPageViewModel,
    SiteLayoutType,
    BreadcrumbItemModel,
    PageMetaData,
} from '@/types/B2BcontentServerContract';
import domService from '@/core/browser/dom.service';
import utils from '@/shared/utilities.service';
import {
    B2CContentPageContent,
    B2CContentPageViewModel,
    B2CPublicContentPageViewModel,
} from '@/types/B2CcontentServerContract';
import siteContextStore from '@/shared/content/site-context.store';
import { StoreContextContentPageContent, StoreContextContentPageViewModel } from '@/types/StoreContextContentServerContract';
import Cookies from 'js-cookie';
import authStore from '@/shared/auth/auth.store';
import siteContextService from '@/b2c/shared/site-context.service';
import { QueryKeys } from '@/shared/config/route-query.config';

export const COUNTRY_USER_COOKIE_NAME = 'country';

interface IPageState {
    pageData: B2BContentPageViewModel | B2CContentPageViewModel | B2CPublicContentPageViewModel | StoreContextContentPageViewModel | null;
    altTitle: string | null;
    dismissedPromotionBanner: boolean;
    topNavigationHeight: number;
    internationalCountryPickerHeight: number;
}

class PageStore {
    private state: IPageState = reactive({
        pageData: null,
        altTitle: null,
        dismissedPromotionBanner: false,
        topNavigationHeight: 0,
        internationalCountryPickerHeight: 0,
    });

    constructor() {
        makeStoreDebugAccessible('spaStore', this.state);
    }

    public get content(): B2BContentPageContent | B2CContentPageContent | StoreContextContentPageContent | null {
        return this.state.pageData?.content ?? null;
    }

    public get contentType(): string | null {
        return this.state.pageData?.contentType ?? null;
    }

    public get metadata(): PageMetaData | null {
        return this.state.pageData?.meta ?? null;
    }

    public get altTitle(): string {
        return this.state.altTitle || '';
    }

    public get title(): string {
        const title = this.state.pageData?.meta.title ?? '';
        return this.altTitle ? utils.formatString(title, this.altTitle) : title;
    }

    public get breadcrumb(): BreadcrumbItemModel[] | null {
        return this.state.pageData?.breadcrumb ?? null;
    }

    public get siteLayoutName(): string {
        return SiteLayoutType[this.state.pageData?.siteLayoutType ?? 0];
    }

    public get isFullSiteLayout(): boolean {
        return this.state.pageData?.siteLayoutType === SiteLayoutType.Full;
    }

    public get isTunnelSiteLayout(): boolean {
        return this.state.pageData?.siteLayoutType === SiteLayoutType.Tunnel;
    }

    public get isCheckoutSiteLayout(): boolean {
        return this.state.pageData?.siteLayoutType === SiteLayoutType.Checkout;
    }

    public get isStoreContextPageLayout(): boolean {
        return this.state.pageData?.contentType === 'StoreContextContentPageViewModel';
    }

    public get isB2CPageLayout(): boolean {
        return this.state.pageData?.contentType === 'B2CContentPageViewModel' ||
            this.state.pageData?.contentType === 'B2CPublicContentPageViewModel';
    }

    public get isPublicB2CPageLayout(): boolean {
        return this.state.pageData?.contentType === 'B2CPublicContentPageViewModel';
    }

    public get isB2BPageLayout(): boolean {
        return this.state.pageData?.contentType === 'B2BContentPageViewModel';
    }

    public get isProductDetailPage(): boolean {
        return this.state.pageData?.meta.pageType === 'ProductDetailPage';
    }

    public get myBoliaLeftNavigationEnabled(): boolean {
        const pageData = this.state.pageData as B2CContentPageViewModel;
        return pageData?.myBoliaLeftNavigationEnabled ?? false;
    }

    public get basketId(): string {
        const pageData = this.state.pageData;
        return pageData?.basketId ?? '';
    }

    public get hasLeftNavigation(): boolean {
        return this.myBoliaLeftNavigationEnabled;
    }

    public get hasActivePromotionBanner(): boolean {
        const hasBanner = siteContextStore.promotionBanner !== null;
        const pageHidden = this.state.pageData?.hidePromotionBanner ?? false;
        const dismissed = this.state.dismissedPromotionBanner;
        return hasBanner && !pageHidden && !dismissed;
    }

    public get hasContentTiles(): boolean {
        const tilesCount: number = this.content?.tiles.length ?? 0;
        return tilesCount > 0;
    }

    public dismissPromotionBanner(): void {
        this.state.dismissedPromotionBanner = true;
    }

    public setInternationalCountryPickerHeight(height: number): void {
        this.state.internationalCountryPickerHeight = height;
    }

    public showInternationalCountryPicker(): boolean {
        const hasRelevantCountryCookie = Cookies.get(COUNTRY_USER_COOKIE_NAME) === 'xx' || Cookies.get(COUNTRY_USER_COOKIE_NAME) === undefined;
        return hasRelevantCountryCookie &&
            !authStore.isSignedInAsStore &&
            !authStore.isSignedInAsPartner &&
            siteContextService.isHaOnlineEnabled;
    }

    public get topNavigationHeight(): number {
        const storeContextBannerHeight: number = this.isStoreContextPageLayout ? 48 : 0;
        const countryPickerBannerHeight: number = this.showInternationalCountryPicker() ? this.state.internationalCountryPickerHeight : 0;
        return this.state.topNavigationHeight + storeContextBannerHeight + countryPickerBannerHeight;
    }

    public setTopNavigationHeight(height: number) {
        this.state.topNavigationHeight = height;
    }

    public setAltTitle(title: string): void {
        this.state.altTitle = title;
    }

    public clearAltTitle(): void {
        this.state.altTitle = null;
    }

    public updatePage(data: B2BContentPageViewModel | B2CContentPageViewModel | B2CPublicContentPageViewModel | null) {
        this.state.pageData = data;
        domService.updateMetaData(this.state.pageData!.meta);
    }

    public get changeCountryUrl(): string {
        return `/?${QueryKeys.ChangeCountry}`;
    }
}

export default new PageStore();
